import { useState, useEffect } from 'react';
import { ChevronLeft, Users, Filter, MapPin, Briefcase, Heart, Star, Bookmark, GraduationCap, User, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { toast } from 'sonner';
import { API_URL } from '../lib/api-client';
import { getDisplayName } from '../utils/privacyUtils';
import { logger } from '../utils/logger';

interface ViewAllProfilesPageProps {
  title?: string;
  filterType?: 'new' | 'more' | 'similar' | 'suggested';
  onBack?: () => void;
  onViewProfile?: (profile: any) => void;
  hideHeader?: boolean;
}

export default function ViewAllProfilesPage({ 
  title = 'All Profiles', 
  filterType = 'more', 
  onBack = () => {}, 
  onViewProfile,
  hideHeader = true
}: ViewAllProfilesPageProps) {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const [shortlistedProfiles, setShortlistedProfiles] = useState<Set<string>>(new Set());
  const [sentInterests, setSentInterests] = useState<Set<string>>(new Set());
  const [loadingInterests, setLoadingInterests] = useState<Set<string>>(new Set());
  const [loadingShortlists, setLoadingShortlists] = useState<Set<string>>(new Set());
  
  // Filter states
  const [ageMin, setAgeMin] = useState(21);
  const [ageMax, setAgeMax] = useState(35);
  const [heightMin, setHeightMin] = useState(4.5);
  const [heightMax, setHeightMax] = useState(6.5);
  const [selectedReligions, setSelectedReligions] = useState<string[]>([]);
  const [selectedCastes, setSelectedCastes] = useState<string[]>([]);
  const [selectedEducations, setSelectedEducations] = useState<string[]>([]);
  const [selectedOccupations, setSelectedOccupations] = useState<string[]>([]);
  const [selectedMaritalStatus, setSelectedMaritalStatus] = useState<string[]>([]);
  const [selectedMotherTongues, setSelectedMotherTongues] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  
  // Filter panel states
  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(new Set(['age', 'height']));
  const [showFilters, setShowFilters] = useState(true);
  
  const profilesPerPage = 16;
  const totalPages = Math.ceil(totalProfiles / profilesPerPage);

  // Filter options
  const religions = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain', 'Parsi', 'Jewish', 'Other'];
  const castes = ['Brahmin', 'Kshatriya', 'Vaishya', 'Shudra', 'Scheduled Caste', 'Scheduled Tribe', 'OBC', 'Others', 'Prefer not to say'];
  const educations = ['High School', "Bachelor's", "Master's", 'PhD', 'Diploma', 'Professional Degree', 'Other'];
  const occupations = ['Software Engineer', 'Doctor', 'Teacher', 'Business Owner', 'Lawyer', 'Government Employee', 'Private Employee', 'Self Employed', 'Other'];
  const maritalStatuses = ['Never Married', 'Divorced', 'Widowed', 'Separated'];
  const motherTongues = ['Hindi', 'English', 'Bengali', 'Telugu', 'Marathi', 'Tamil', 'Gujarati', 'Kannada', 'Malayalam', 'Punjabi', 'Urdu', 'Other'];
  const states = ['Maharashtra', 'Delhi', 'Karnataka', 'Tamil Nadu', 'Gujarat', 'Uttar Pradesh', 'West Bengal', 'Rajasthan', 'Kerala', 'Punjab', 'Other'];
  const cities = ['Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 'Kolkata', 'Pune', 'Ahmedabad', 'Surat', 'Jaipur', 'Other'];

  useEffect(() => {
    fetchProfiles(currentPage);
  }, [currentPage, filterType]);

  const applyFilters = () => {
    setCurrentPage(1); // Reset to first page
    fetchProfiles(1);
    toast.success('Filters applied successfully!');
  };

  // Fetch shortlisted profiles on mount
  useEffect(() => {
    const fetchShortlistedProfiles = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${API_URL}/interactions/sent?type=shortlist`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const interactions = data.data?.interactions || data.data || [];
            const shortlistedIds = interactions
              .filter((int: any) => int.status === 'shortlist')
              .map((int: any) => int.toProfile?._id || int.toProfile || int.toUser?._id || int.toUser)
              .filter(Boolean);
            setShortlistedProfiles(new Set(shortlistedIds));
          }
        }
      } catch (error) {
        logger.log('Failed to fetch shortlisted profiles:', error);
      }
    };

    const fetchSentInterests = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${API_URL}/interactions/sent?type=interest`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const interactions = data.data?.interactions || data.data || [];
            const sentIds = interactions
              .map((int: any) => int.toProfile?._id || int.toProfile || int.toUser?._id || int.toUser)
              .filter(Boolean);
            setSentInterests(new Set(sentIds));
          }
        }
      } catch (error) {
        logger.log('Failed to fetch sent interests:', error);
      }
    };

    fetchShortlistedProfiles();
    fetchSentInterests();
  }, []);

  const fetchProfiles = async (page: number) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Build filter query params
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', profilesPerPage.toString());
      
      // Add filter type
      if (filterType === 'new') {
        params.append('filterType', 'new');
      }
      
      // Add age filters
      if (ageMin) params.append('ageMin', ageMin.toString());
      if (ageMax) params.append('ageMax', ageMax.toString());
      
      // Add height filters
      if (heightMin) params.append('heightMin', heightMin.toString());
      if (heightMax) params.append('heightMax', heightMax.toString());
      
      // Add array filters
      if (selectedReligions.length > 0) {
        selectedReligions.forEach(r => params.append('religion', r));
      }
      if (selectedMaritalStatus.length > 0) {
        selectedMaritalStatus.forEach(m => params.append('maritalStatus', m));
      }
      if (selectedEducations.length > 0) {
        selectedEducations.forEach(e => params.append('education', e));
      }
      if (selectedOccupations.length > 0) {
        selectedOccupations.forEach(o => params.append('occupation', o));
      }
      if (selectedMotherTongues.length > 0) {
        selectedMotherTongues.forEach(m => params.append('motherTongue', m));
      }
      if (selectedStates.length > 0) {
        selectedStates.forEach(s => params.append('state', s));
      }
      if (selectedCities.length > 0) {
        selectedCities.forEach(c => params.append('city', c));
      }
      if (selectedCastes.length > 0) {
        selectedCastes.forEach(c => params.append('caste', c));
      }

      const targetApiUrl = (API_URL && API_URL.startsWith('http')) ? API_URL : 'http://localhost:5001/api';
      let url = '';
      
      // Build URL based on filter type
      if (filterType === 'new') {
        url = `${targetApiUrl}/profiles?${params.toString()}`;
      } else if (filterType === 'more') {
        url = `${targetApiUrl}/profiles/discovery?${params.toString()}`;
      } else if (filterType === 'similar') {
        url = `${targetApiUrl}/profiles/discovery?${params.toString()}`;
      } else if (filterType === 'suggested') {
        url = `${targetApiUrl}/profiles/discovery?${params.toString()}`;
      }

      logger.log('Fetching profiles with filters:', url);

      const response = await fetch(url, { headers });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          setProfiles([]);
          setTotalProfiles(0);
          setLoading(false);
          return;
        }
        setProfiles([]);
        setTotalProfiles(0);
        setLoading(false);
        return;
      }

      const data = await response.json();
      
      // Parse response
      let profilesArray = data.data?.profiles || data.profiles || [];

      // Strict client-side filter verification safety check
      if (selectedReligions.length > 0) {
        profilesArray = profilesArray.filter((p: any) => {
          const pRel = (p.religion || p.basicInfo?.religion || '').toLowerCase();
          return selectedReligions.some(r => pRel.includes(r.toLowerCase()));
        });
      }

      if (selectedCastes.length > 0) {
        profilesArray = profilesArray.filter((p: any) => {
          const pCaste = (p.caste || p.basicInfo?.caste || '').toLowerCase();
          return selectedCastes.some(c => pCaste.includes(c.toLowerCase()));
        });
      }

      if (selectedCities.length > 0) {
        profilesArray = profilesArray.filter((p: any) => {
          const pCity = (p.city || p.location?.city || '').toLowerCase();
          return selectedCities.some(c => pCity.includes(c.toLowerCase()));
        });
      }

      if (selectedStates.length > 0) {
        profilesArray = profilesArray.filter((p: any) => {
          const pState = (p.state || p.location?.state || '').toLowerCase();
          return selectedStates.some(s => pState.includes(s.toLowerCase()));
        });
      }

      const paginationData = data.data?.pagination || data.pagination;
      const total = paginationData?.total || profilesArray.length;
      
      setProfiles(profilesArray);
      setTotalProfiles(profilesArray.length);
      
    } catch (error) {
      logger.log('Error fetching profiles:', error);
      setProfiles([]);
      setTotalProfiles(0);
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth: string) => {
    try {
      const birthDate = new Date(dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch {
      return 25;
    }
  };

  const heightToFeetInches = (height: any) => {
    try {
      if (typeof height === 'string' && height.includes("'")) {
        return height;
      }
      if (typeof height === 'number') {
        const totalInches = height / 2.54;
        const feet = Math.floor(totalInches / 12);
        const inches = Math.round(totalInches % 12);
        return `${feet}'${inches}"`;
      }
      return "5'5\"";
    } catch {
      return "5'5\"";
    }
  };

  const handleSendInterest = async (profileId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to send interest');
        return;
      }

      setLoadingInterests(prev => new Set([...prev, profileId]));
      const response = await fetch(`${API_URL}/interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          profileId,
          type: 'interest'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        if (data.match) {
          toast.success("It's a Match! 💕", {
            description: 'You can now start chatting!',
            duration: 5000
          });
        } else {
          toast.success('Interest sent successfully! 💌');
        }
        setSentInterests(prev => new Set([...prev, profileId]));
      } else {
        toast.error(data.message || 'Failed to send interest');
      }
    } catch (error) {
      logger.log('Error sending interest:', error);
      toast.error('Failed to send interest');
    } finally {
      setLoadingInterests(prev => {
        const newSet = new Set(prev);
        newSet.delete(profileId);
        return newSet;
      });
    }
  };

  const handleShortlist = async (profileId: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to shortlist');
        return;
      }

      const isShortlisted = shortlistedProfiles.has(profileId);
      setLoadingShortlists(prev => new Set([...prev, profileId]));
      
      const response = await fetch(`${API_URL}/interactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          profileId,
          type: 'shortlist'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        if (isShortlisted) {
          // Remove from shortlist
          setShortlistedProfiles(prev => {
            const newSet = new Set(prev);
            newSet.delete(profileId);
            return newSet;
          });
          toast.success('Removed from shortlist!');
        } else {
          // Add to shortlist
          setShortlistedProfiles(prev => new Set([...prev, profileId]));
          toast.success('Added to shortlist!');
        }
      } else {
        toast.error(data.message || 'Failed to shortlist');
      }
    } catch (error) {
      logger.log('Error shortlisting profile:', error);
      toast.error('Failed to shortlist');
    } finally {
      setLoadingShortlists(prev => {
        const newSet = new Set(prev);
        newSet.delete(profileId);
        return newSet;
      });
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      
      if (start > 2) {
        pages.push(-1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (end < totalPages - 1) {
        pages.push(-2);
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="w-full min-h-full bg-gradient-to-br from-rose-50 via-white to-pink-50">
      {/* Header */}
      {!hideHeader && (
        <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
          <div className="w-full px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={onBack}
                  className="p-2.5 hover:bg-rose-50 rounded-xl transition-all group"
                  aria-label="Go back"
                >
                  <ChevronLeft className="h-6 w-6 text-gray-700 group-hover:text-rose-600 transition-colors" />
                </button>
                <div className="border-l border-gray-300 pl-4">
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                    {title}
                  </h1>
                  {/* Hide profile count on mobile, show on desktop */}
                  <div className="hidden md:flex items-center gap-4 mt-1">
                    <p className="text-sm text-gray-600 flex items-center gap-1.5">
                      <Users className="h-4 w-4 text-rose-500" />
                      <span className="font-semibold text-gray-900">{totalProfiles}</span> profiles found
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Filter Badge */}
              <div className="hidden md:flex items-center gap-2">
                <Badge className="bg-gradient-to-r from-rose-100 to-pink-100 text-rose-700 border-0 px-4 py-2">
                  <Filter className="h-3.5 w-3.5 mr-1.5" />
                  {filterType === 'new' ? 'New Matches' : filterType === 'similar' ? 'Similar Profiles' : filterType === 'suggested' ? 'Suggested For You' : 'More Matches'}
                </Badge>
              </div>
              
              {/* Mobile Filter Button */}
              <button
                onClick={() => setShowFilters(true)}
                className="lg:hidden p-2.5 hover:bg-rose-50 rounded-xl transition-all"
                aria-label="Show filters"
              >
                <Filter className="h-5 w-5 text-gray-700" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Filter Modal */}
      {showFilters && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setShowFilters(false)}>
          <div 
            className="absolute inset-x-0 bottom-0 bg-white rounded-t-3xl max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-3xl z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Filters</h2>
                <button
                  onClick={() => setShowFilters(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-4">
              {/* Age Filter */}
              <div className="border-b border-gray-100 pb-4">
                <button
                  onClick={() => {
                    const newSet = new Set(expandedFilters);
                    if (newSet.has('age')) {
                      newSet.delete('age');
                    } else {
                      newSet.add('age');
                    }
                    setExpandedFilters(newSet);
                  }}
                  className="flex items-center justify-between w-full"
                >
                  <span className="font-semibold text-gray-800">Age</span>
                  {expandedFilters.has('age') ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
                {expandedFilters.has('age') && (
                  <div className="mt-4 space-y-2">
                    <div className="flex gap-3">
                      <input
                        type="number"
                        value={ageMin}
                        onChange={(e) => setAgeMin(Number(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base"
                        placeholder="Min Age"
                      />
                      <input
                        type="number"
                        value={ageMax}
                        onChange={(e) => setAgeMax(Number(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base"
                        placeholder="Max Age"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Height Filter */}
              <div className="border-b border-gray-100 pb-4">
                <button
                  onClick={() => {
                    const newSet = new Set(expandedFilters);
                    if (newSet.has('height')) {
                      newSet.delete('height');
                    } else {
                      newSet.add('height');
                    }
                    setExpandedFilters(newSet);
                  }}
                  className="flex items-center justify-between w-full"
                >
                  <span className="font-semibold text-gray-800">Height</span>
                  {expandedFilters.has('height') ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </button>
                {expandedFilters.has('height') && (
                  <div className="mt-4 space-y-2">
                    <div className="flex gap-3">
                      <select
                        value={heightMin}
                        onChange={(e) => setHeightMin(Number(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base"
                      >
                        {[4, 4.5, 5, 5.5, 6, 6.5, 7].map(h => (
                          <option key={h} value={h}>{h}'</option>
                        ))}
                      </select>
                      <select
                        value={heightMax}
                        onChange={(e) => setHeightMax(Number(e.target.value))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base"
                      >
                        {[4, 4.5, 5, 5.5, 6, 6.5, 7].map(h => (
                          <option key={h} value={h}>{h}'</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Religion Filter */}
              <FilterCheckboxSection
                title="Religion"
                options={religions}
                selected={selectedReligions}
                setSelected={setSelectedReligions}
                expanded={expandedFilters.has('religion')}
                onToggle={() => {
                  const newSet = new Set(expandedFilters);
                  if (newSet.has('religion')) {
                    newSet.delete('religion');
                  } else {
                    newSet.add('religion');
                  }
                  setExpandedFilters(newSet);
                }}
              />

              {/* Marital Status Filter */}
              <FilterCheckboxSection
                title="Marital Status"
                options={maritalStatuses}
                selected={selectedMaritalStatus}
                setSelected={setSelectedMaritalStatus}
                expanded={expandedFilters.has('marital')}
                onToggle={() => {
                  const newSet = new Set(expandedFilters);
                  if (newSet.has('marital')) {
                    newSet.delete('marital');
                  } else {
                    newSet.add('marital');
                  }
                  setExpandedFilters(newSet);
                }}
              />

              {/* Education Filter */}
              <FilterCheckboxSection
                title="Education"
                options={educations}
                selected={selectedEducations}
                setSelected={setSelectedEducations}
                expanded={expandedFilters.has('education')}
                onToggle={() => {
                  const newSet = new Set(expandedFilters);
                  if (newSet.has('education')) {
                    newSet.delete('education');
                  } else {
                    newSet.add('education');
                  }
                  setExpandedFilters(newSet);
                }}
              />

              {/* State Filter */}
              <FilterCheckboxSection
                title="State"
                options={states}
                selected={selectedStates}
                setSelected={setSelectedStates}
                expanded={expandedFilters.has('state')}
                onToggle={() => {
                  const newSet = new Set(expandedFilters);
                  if (newSet.has('state')) {
                    newSet.delete('state');
                  } else {
                    newSet.add('state');
                  }
                  setExpandedFilters(newSet);
                }}
              />

              {/* City Filter */}
              <FilterCheckboxSection
                title="City"
                options={cities}
                selected={selectedCities}
                setSelected={setSelectedCities}
                expanded={expandedFilters.has('city')}
                onToggle={() => {
                  const newSet = new Set(expandedFilters);
                  if (newSet.has('city')) {
                    newSet.delete('city');
                  } else {
                    newSet.add('city');
                  }
                  setExpandedFilters(newSet);
                }}
              />
            </div>

            {/* Sticky Bottom Actions */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 space-y-3">
              <button
                onClick={() => {
                  applyFilters();
                  setShowFilters(false);
                }}
                className="w-full py-3.5 bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-xl font-semibold hover:from-rose-600 hover:to-pink-600 transition-all shadow-lg"
              >
                Apply Filters
              </button>
              <button
                onClick={() => {
                  setAgeMin(21);
                  setAgeMax(35);
                  setHeightMin(4.5);
                  setHeightMax(6.5);
                  setSelectedReligions([]);
                  setSelectedCastes([]);
                  setSelectedEducations([]);
                  setSelectedOccupations([]);
                  setSelectedMaritalStatus([]);
                  setSelectedMotherTongues([]);
                  setSelectedCities([]);
                  setSelectedStates([]);
                  toast.info('All filters cleared');
                }}
                className="w-full py-3.5 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
              >
                Clear All Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="w-full px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar Filters - Mobile: Fixed full screen overlay, Desktop: Sticky sidebar */}
          <div className={`${showFilters ? 'w-72' : 'w-0'} flex-shrink-0 transition-all duration-300 overflow-hidden hidden lg:block`}>
            <div className="sticky top-20 bg-white rounded-2xl shadow-sm border border-gray-100 p-5 max-h-[calc(100vh-96px)] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-gray-900">Filters</h2>
              </div>

              {/* Filter Sections */}
              <div className="space-y-3">
                {/* Age Filter */}
                <div className="border-b border-gray-100 pb-3">
                  <button
                    onClick={() => {
                      const newSet = new Set(expandedFilters);
                      if (newSet.has('age')) {
                        newSet.delete('age');
                      } else {
                        newSet.add('age');
                      }
                      setExpandedFilters(newSet);
                    }}
                    className="flex items-center justify-between w-full"
                  >
                    <span className="font-medium text-gray-700">Age</span>
                    {expandedFilters.has('age') ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {expandedFilters.has('age') && (
                    <div className="mt-3 space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={ageMin}
                          onChange={(e) => setAgeMin(Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          placeholder="Min"
                        />
                        <input
                          type="number"
                          value={ageMax}
                          onChange={(e) => setAgeMax(Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                          placeholder="Max"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Height Filter */}
                <div className="border-b border-gray-100 pb-3">
                  <button
                    onClick={() => {
                      const newSet = new Set(expandedFilters);
                      if (newSet.has('height')) {
                        newSet.delete('height');
                      } else {
                        newSet.add('height');
                      }
                      setExpandedFilters(newSet);
                    }}
                    className="flex items-center justify-between w-full"
                  >
                    <span className="font-medium text-gray-700">Height</span>
                    {expandedFilters.has('height') ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {expandedFilters.has('height') && (
                    <div className="mt-3 space-y-2">
                      <div className="flex gap-2">
                        <select
                          value={heightMin}
                          onChange={(e) => setHeightMin(Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        >
                          {[4, 4.5, 5, 5.5, 6, 6.5, 7].map(h => (
                            <option key={h} value={h}>{h}'</option>
                          ))}
                        </select>
                        <select
                          value={heightMax}
                          onChange={(e) => setHeightMax(Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        >
                          {[4, 4.5, 5, 5.5, 6, 6.5, 7].map(h => (
                            <option key={h} value={h}>{h}'</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Religion Filter */}
                <FilterCheckboxSection
                  title="Religion"
                  options={religions}
                  selected={selectedReligions}
                  setSelected={setSelectedReligions}
                  expanded={expandedFilters.has('religion')}
                  onToggle={() => {
                    const newSet = new Set(expandedFilters);
                    if (newSet.has('religion')) {
                      newSet.delete('religion');
                    } else {
                      newSet.add('religion');
                    }
                    setExpandedFilters(newSet);
                  }}
                />

                {/* Marital Status Filter */}
                <FilterCheckboxSection
                  title="Marital Status"
                  options={maritalStatuses}
                  selected={selectedMaritalStatus}
                  setSelected={setSelectedMaritalStatus}
                  expanded={expandedFilters.has('marital')}
                  onToggle={() => {
                    const newSet = new Set(expandedFilters);
                    if (newSet.has('marital')) {
                      newSet.delete('marital');
                    } else {
                      newSet.add('marital');
                    }
                    setExpandedFilters(newSet);
                  }}
                />

                {/* Education Filter */}
                <FilterCheckboxSection
                  title="Education"
                  options={educations}
                  selected={selectedEducations}
                  setSelected={setSelectedEducations}
                  expanded={expandedFilters.has('education')}
                  onToggle={() => {
                    const newSet = new Set(expandedFilters);
                    if (newSet.has('education')) {
                      newSet.delete('education');
                    } else {
                      newSet.add('education');
                    }
                    setExpandedFilters(newSet);
                  }}
                />

                {/* State Filter */}
                <FilterCheckboxSection
                  title="State"
                  options={states}
                  selected={selectedStates}
                  setSelected={setSelectedStates}
                  expanded={expandedFilters.has('state')}
                  onToggle={() => {
                    const newSet = new Set(expandedFilters);
                    if (newSet.has('state')) {
                      newSet.delete('state');
                    } else {
                      newSet.add('state');
                    }
                    setExpandedFilters(newSet);
                  }}
                />

                {/* City Filter */}
                <FilterCheckboxSection
                  title="City"
                  options={cities}
                  selected={selectedCities}
                  setSelected={setSelectedCities}
                  expanded={expandedFilters.has('city')}
                  onToggle={() => {
                    const newSet = new Set(expandedFilters);
                    if (newSet.has('city')) {
                      newSet.delete('city');
                    } else {
                      newSet.add('city');
                    }
                    setExpandedFilters(newSet);
                  }}
                />
              </div>

              {/* Apply Filters Button */}
              <button
                onClick={applyFilters}
                className="w-full mt-6 px-4 py-2.5 border-2 border-rose-500 text-rose-600 rounded-xl hover:bg-rose-50 transition-all font-medium"
              >
                Apply Filters
              </button>

              {/* Clear All button */}
              <button
                onClick={() => {
                  setAgeMin(21);
                  setAgeMax(35);
                  setHeightMin(4.5);
                  setHeightMax(6.5);
                  setSelectedReligions([]);
                  setSelectedCastes([]);
                  setSelectedEducations([]);
                  setSelectedOccupations([]);
                  setSelectedMaritalStatus([]);
                  setSelectedMotherTongues([]);
                  setSelectedCities([]);
                  setSelectedStates([]);
                  toast.info('All filters cleared');
                }}
                className="w-full mt-6 px-4 py-2.5 border-2 border-rose-500 text-rose-600 rounded-xl hover:bg-rose-50 transition-all font-medium"
              >
                Clear All Filters
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">{[...Array(16)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
                  <div className="h-72 bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse" />
                  <div className="p-5 space-y-3">
                    <div className="h-6 bg-gray-200 animate-pulse rounded-lg w-3/4" />
                    <div className="h-4 bg-gray-100 animate-pulse rounded w-1/2" />
                    <div className="space-y-2 pt-2">
                      <div className="h-4 bg-gray-100 animate-pulse rounded w-full" />
                      <div className="h-4 bg-gray-100 animate-pulse rounded w-2/3" />
                    </div>
                    <div className="h-10 bg-gradient-to-r from-pink-100 to-rose-100 animate-pulse rounded-lg mt-4" />
                  </div>
                </div>
              ))}
              </div>
            ) : profiles.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-32">
                <div className="bg-white rounded-3xl p-12 shadow-lg border border-gray-100">
                  <Users className="h-24 w-24 text-gray-300 mb-6 mx-auto" />
                  <h3 className="text-2xl font-bold text-gray-800 mb-3 text-center">No Profiles Found</h3>
                  <p className="text-gray-500 text-center max-w-md">
                    We couldn't find any profiles matching your criteria.
                  </p>
                  <Button 
                    onClick={onBack}
                    className="mt-6 mx-auto flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Go Back
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Profiles Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
                  {profiles.map((profile) => {
                    // Safe string extraction helper
                    const safeString = (value: any, fallback: string = 'N/A') => {
                      if (!value) return fallback;
                      if (typeof value === 'string') return value;
                      if (typeof value === 'number') return String(value);
                      if (typeof value === 'object') {
                        return value.name || value.title || value.level || value.field || value.value || fallback;
                      }
                      return fallback;
                    };

                    const ageValue = profile.dateOfBirth ? calculateAge(profile.dateOfBirth) : (profile.age || 25);
                    const age = String(ageValue); // Force to string
                    const height = heightToFeetInches(profile.height);
                    
                    // Extract name from userId or profile itself
                    const firstName = profile.userId?.firstName || profile.firstName || 'User';
                    const lastName = profile.userId?.lastName || profile.lastName || '';
                    
                    const displayNameRaw = getDisplayName(
                      firstName,
                      lastName,
                      false,
                      profile._id || profile.userId?._id
                    );
                    const displayName = safeString(displayNameRaw, 'User'); // Force to string
                    const mainPhoto = profile.photos?.[0]?.url || profile.profilePhoto || '';
                    
                    // Handle all fields safely
                    const education = safeString(profile.education);
                    const occupation = safeString(profile.occupation) !== 'N/A' ? safeString(profile.occupation) : safeString(profile.profession);
                    const city = safeString(profile.city, 'Location N/A');
                    const religion = safeString(profile.religion);

                    if (!profile._id && !profile.id) {
                      return null;
                    }

                    return (
                      <div
                        key={profile._id || profile.id}
                        className="group bg-white rounded-2xl shadow-sm hover:shadow-xl overflow-hidden border border-gray-100 transition-all duration-300 cursor-pointer hover:-translate-y-1"
                        onClick={() => onViewProfile(profile)}
                      >
                        <div className="relative h-72 overflow-hidden">
                          <ImageWithFallback
                            src={mainPhoto}
                            alt={displayName}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          
                          {/* Gradient Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          
                          {/* Badges */}
                          <div className="absolute top-3 left-3 flex flex-col gap-2">
                            {profile.isPromoted && (
                              <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-lg">
                                <Star className="h-3 w-3 mr-1" />
                                Promoted
                              </Badge>
                            )}
                            {profile.isSponsored && (
                              <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 shadow-lg">
                                <Star className="h-3 w-3 mr-1 fill-white" />
                                Featured
                              </Badge>
                            )}
                          </div>

                          {/* Shortlist Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShortlist(profile._id);
                            }}
                            disabled={loadingShortlists.has(profile._id)}
                            className={`absolute top-3 right-3 p-2.5 backdrop-blur-sm rounded-full transition-all duration-300 shadow-lg ${
                              shortlistedProfiles.has(profile._id)
                                ? 'bg-rose-500 text-white'
                                : 'bg-white/95 hover:bg-rose-500 hover:text-white'
                            } disabled:opacity-60`}
                            aria-label="Shortlist profile"
                          >
                            {loadingShortlists.has(profile._id) ? (
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <Bookmark 
                                className={`h-4 w-4 transition-colors ${
                                  shortlistedProfiles.has(profile._id)
                                    ? 'fill-white text-white'
                                    : 'text-gray-700'
                                }`} 
                              />
                            )}
                          </button>

                          {/* Online Status */}
                          {profile.isOnline && (
                            <div className="absolute bottom-3 left-3">
                              <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-500/90 backdrop-blur-sm text-white text-xs rounded-full">
                                <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                                Online
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="p-5">
                          {/* Name and Age */}
                          <div className="mb-3">
                            <h3 className="text-lg font-bold text-gray-900 mb-1 truncate">
                              {displayName}, {age}
                            </h3>
                            <div className="flex items-center gap-1.5 text-sm text-gray-600">
                              <MapPin className="h-3.5 w-3.5 text-rose-500 flex-shrink-0" />
                              <span className="truncate">{city}</span>
                            </div>
                          </div>

                          {/* Profile Details */}
                          <div className="space-y-2 mb-4 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{height} • {religion}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Briefcase className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{occupation}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <GraduationCap className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{education}</span>
                            </div>
                          </div>

                          {/* Send Interest Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSendInterest(profile._id);
                            }}
                            disabled={loadingInterests.has(profile._id) || sentInterests.has(profile._id)}
                            className={`w-full py-2.5 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 font-medium shadow-md hover:shadow-lg ${
                              sentInterests.has(profile._id)
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600'
                            } text-white disabled:opacity-70`}
                          >
                            {loadingInterests.has(profile._id) ? (
                              <>
                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Sending...</span>
                              </>
                            ) : sentInterests.has(profile._id) ? (
                              <>
                                <Heart className="h-4 w-4 fill-white" />
                                <span>Interest Sent</span>
                              </>
                            ) : (
                              <>
                                <Heart className="h-4 w-4" />
                                <span>Send Interest</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex flex-col items-center gap-6 py-8 pb-16 md:pb-20">
                    <p className="text-sm text-gray-600 hidden md:block">
                      Showing page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
                      <span className="font-semibold text-gray-900">{totalPages}</span> 
                      {' '}({totalProfiles} total profiles)
                    </p>
                    <p className="text-sm text-gray-600 md:hidden">
                      Page <span className="font-semibold text-gray-900">{currentPage}</span> of{' '}
                      <span className="font-semibold text-gray-900">{totalPages}</span>
                    </p>
                    
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="px-6 py-3 border-2 border-gray-300 rounded-xl hover:bg-rose-50 hover:border-rose-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-gray-300 transition-all font-medium text-gray-700 hover:text-rose-600"
                      >
                        ← Previous
                      </button>

                      <div className="flex items-center gap-2">
                        {renderPageNumbers().map((page, index) => {
                          if (page === -1 || page === -2) {
                            return (
                              <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-400 font-medium">
                                ...
                              </span>
                            );
                          }
                          
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`min-w-[44px] h-11 px-4 rounded-xl font-medium transition-all duration-300 ${
                                currentPage === page
                                  ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-lg scale-105'
                                  : 'border-2 border-gray-300 hover:border-rose-300 hover:bg-rose-50 text-gray-700 hover:text-rose-600'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                      </div>

                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="px-6 py-3 border-2 border-gray-300 rounded-xl hover:bg-rose-50 hover:border-rose-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-gray-300 transition-all font-medium text-gray-700 hover:text-rose-600"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component for filter checkbox sections
interface FilterCheckboxSectionProps {
  title: string;
  options: string[];
  selected: string[];
  setSelected: (value: string[]) => void;
  expanded: boolean;
  onToggle: () => void;
}

function FilterCheckboxSection({
  title,
  options,
  selected,
  setSelected,
  expanded,
  onToggle
}: FilterCheckboxSectionProps) {
  return (
    <div className="border-b border-gray-100 pb-4">
      <button
        onClick={onToggle}
        className="flex items-center justify-between w-full"
      >
        <span className="font-medium text-gray-700">{title}</span>
        {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {expanded && (
        <div className="mt-3 space-y-2">
          {options.map(option => (
            <label key={option} className="flex items-center gap-2">
              <input
                type="checkbox"
                value={option}
                checked={selected.includes(option)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelected([...selected, option]);
                  } else {
                    setSelected(selected.filter(opt => opt !== option));
                  }
                }}
                className="h-4 w-4 text-rose-600 bg-gray-100 border-gray-300 rounded focus:ring-rose-500"
              />
              <span className="text-sm text-gray-600">{option}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}